import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Tooltip,
} from 'recharts';

import { ChartContainer } from '@/components/charts/ChartContainer';
import type { RadarDataPoint } from '@/sections/customerManagement/types';
import { getMetricHexByLabel } from '@/sections/customerManagement/utils/leaderboardTransforms';

interface Props {
  data: RadarDataPoint[];
}

export function MetricRadarChart({ data }: Props) {
  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-chariot-text-secondary text-sm">
          No metric data available.
        </p>
      </div>
    );
  }

  return (
    <div className="h-80">
      <ChartContainer>
        <RadarChart
          data={data}
          margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
        >
          <PolarGrid className="stroke-chariot-stroke" />
          <PolarAngleAxis
            dataKey="metric"
            tick={({ payload, x, y, textAnchor }) => (
              <text
                x={x}
                y={y}
                textAnchor={textAnchor}
                fill={getMetricHexByLabel(payload.value)}
                fontSize={12}
              >
                {payload.value}
              </text>
            )}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={false}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            fill="transparent"
            stroke="transparent"
            shape={shapeProps => {
              const { points } = shapeProps as {
                points: Array<{
                  x: number;
                  y: number;
                  cx: number;
                  cy: number;
                }>;
              };
              if (!points?.length) return <g />;
              const ctrX = points[0].cx;
              const ctrY = points[0].cy;
              const n = points.length;
              return (
                <g>
                  <defs>
                    {points.map((pt, i) => {
                      const j = (i + 1) % n;
                      const next = points[j];
                      const vI = data[i].value || 1;
                      const vJ = data[j].value || 1;
                      const opI = 0.15 + (vI / 100) * 0.6;
                      const opJ = 0.15 + (vJ / 100) * 0.6;
                      const colorI = getMetricHexByLabel(data[i].metric);
                      const colorJ = getMetricHexByLabel(data[j].metric);
                      const cross = ((vI / (vI + vJ)) * 100).toFixed(1);
                      return (
                        <linearGradient
                          key={`g${i}`}
                          id={`rs${i}`}
                          gradientUnits="userSpaceOnUse"
                          x1={String(pt.x)}
                          y1={String(pt.y)}
                          x2={String(next.x)}
                          y2={String(next.y)}
                        >
                          <stop
                            offset="0%"
                            stopColor={colorI}
                            stopOpacity={opI}
                          />
                          <stop
                            offset={`${cross}%`}
                            stopColor={vI >= vJ ? colorI : colorJ}
                            stopOpacity={(opI + opJ) / 2}
                          />
                          <stop
                            offset="100%"
                            stopColor={colorJ}
                            stopOpacity={opJ}
                          />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  {points.map((pt, i) => {
                    const next = points[(i + 1) % n];
                    return (
                      <path
                        key={`f${i}`}
                        d={`M${ctrX},${ctrY}L${pt.x},${pt.y}L${next.x},${next.y}Z`}
                        fill={`url(#rs${i})`}
                        stroke={`url(#rs${i})`}
                        strokeWidth={1}
                        strokeOpacity={0.3}
                        strokeLinejoin="round"
                      />
                    );
                  })}
                  {points.map((pt, i) => (
                    <circle
                      key={`d${i}`}
                      cx={pt.x}
                      cy={pt.y}
                      r={3}
                      fill={getMetricHexByLabel(data[i].metric)}
                    />
                  ))}
                </g>
              );
            }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload?.[0]) return null;
              const point = payload[0].payload as RadarDataPoint;
              return (
                <div className="border-chariot-stroke bg-chariot-background-primary rounded border p-2 text-xs shadow">
                  <p className="text-chariot-text-primary font-medium">
                    {point.metric}
                  </p>
                  <p className="text-chariot-text-secondary">
                    Raw: {point.rawValue}
                  </p>
                  <p className="text-chariot-text-secondary">
                    Percentile: {point.value}%
                  </p>
                </div>
              );
            }}
          />
        </RadarChart>
      </ChartContainer>
    </div>
  );
}
